import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBuilding,
  faFileAlt,
  faShieldAlt,
  faUserMd,
} from '@fortawesome/free-solid-svg-icons';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import ErrorComponent from '../../common/components/ErrorComponent';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { getProfessioneSanitaCompletedData, professionieSanitaCompletedDataReset } from '../../features/purchases/purchasesActions';
import { apiDownloadDocument, apiDownloadOptionalDocument } from '../../services/axios-client/axeCommons';
import { ProfessioniSanitaCompletedScreenStyles } from '../CompletedQuotes/completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from '../CompletedQuotes/CompletedV2.styles';
import ContractDocuments from '../../common/components/elements/ContractDocuments';
import CompletedQuoteScreenInfoProfessioniSanita from './components/CompletedQuoteScreenInfoProfessioniSanita';
import CompletedScreenProfessionSanitaDocuments from './components/CompletedScreenProfessionSanitaDocuments';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';
import { professionEmployeeTypes } from '../../common/constants';

function ProfessionaleSanitaCompletedScreen() {
  const [residenceInfo, setResidenceInfo] = useState(null);
  const { quoteId } = useParams();
  const {
    loading, quote, error, risk_quotations,
  } = useSelector(
    (store) => store.purchases.rc_professione_sanita_completed,
  );
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find(
          (commune) => commune.cadastral_code
            == quote.insurance_request.customer.residence_commune_code,
        );
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const downloadOptionalDocument = async (documentId) => {
    const response = await apiDownloadOptionalDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const collectQuoteData = () => {
    getProfessioneSanitaCompletedData(quoteId);
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      professionieSanitaCompletedDataReset();
    };
  }, [quoteId]);

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  const getSubProductName = (subProduct) => {
    if (subProduct === 'professioni_sanitarie') return 'Professioni Sanitarie';
    if (subProduct === 'medici_dipendenti') return 'Medici Dipendenti';
    if (subProduct === 'medico_protetto') return 'Medico Protetto';

    return '';
  };

  if (loading) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }

  const documentsRequests = [
    ...quote.insurance_request.document_requests.filter(
      (dr) => dr.document !== null,
    ),
    ...quote.document_requests.filter((dr) => dr.document !== null),
  ];

  return (
    <ProfessioniSanitaCompletedScreenStyles>
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocuments
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
          v2
        />
      </div>

      {/* Customer & Professional Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon
                    icon={quote.insurance_request.customer.gender === 'G' ? faBuilding : faUser}
                  />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>
                    {quote.insurance_request.customer.gender === 'G' ? 'Compagnia' : 'Contraente'}
                  </dt>
                  <dd>
                    {quote.insurance_request.customer.gender === 'G'
                      ? quote.insurance_request.customer.business_name
                      : `${quote.insurance_request.customer.name} ${quote.insurance_request.customer.surname}`}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{quote.insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{quote.insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {quote.insurance_request.customer.address}
                    ,
                    {' '}
                    {quote.insurance_request.customer.house_number}
                    ,
                    {' '}
                    {quote.insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Prodotto</dt>
                  <dd>{quote.product.name}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Sub Prodotto</dt>
                  <dd>{getSubProductName(quote.sub_product)}</dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>

          {/* Professional Information Card */}
          {quote.insurance_request.health_profession && (
            <div>
              <CompletedCard>
                <CompletedCardHeader>
                  <CompletedCardIcon>
                    <FontAwesomeIcon icon={faUserMd} />
                  </CompletedCardIcon>
                  <CompletedCardTitle>Dati Professionali</CompletedCardTitle>
                </CompletedCardHeader>

                <CompletedFieldGrid>
                  <CompletedField className="full">
                    <dt>Retroattività</dt>
                    <dd>
                      {quote.insurance_request.health_profession.retroactivity === '10_anni' ? '10 Anni' : 'Illimitata'}
                    </dd>
                  </CompletedField>

                  {quote.insurance_request.health_profession.medico_protetto_activity && (
                    <CompletedField className="full">
                      <dt>Attività</dt>
                      <dd>
                        {quote.insurance_request.health_profession.medico_protetto_activity.name}
                      </dd>
                    </CompletedField>
                  )}
                  {quote.insurance_request.health_profession && (
                    <>
                      {quote.insurance_request.health_profession.health_profession && (
                        <CompletedField className="full">
                          <dt>Attività</dt>
                          <dd>
                            {quote.insurance_request.health_profession.health_profession.name}
                          </dd>
                        </CompletedField>
                      )}
                      {quote.insurance_request.health_profession.employee_type && (
                        <CompletedField>
                          <dt>
                            Tipo Dipendente
                          </dt>
                          <dd>
                            {professionEmployeeTypes.find((el) => el.id === quote.insurance_request.health_profession.employee_type).name}
                          </dd>
                        </CompletedField>
                      )}
                    </>
                  )}
                </CompletedFieldGrid>
              </CompletedCard>
            </div>
          )}
        </div>
      </div>

      {/* Quote Details & Documents Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Preventivo e Documenti</h2>
        </CompletedSectionTitle>

        <div className="row">
          <CompletedQuoteScreenInfoProfessioniSanita
            rc_quotation={quote}
            risk_quotations={risk_quotations}
          />

          <CompletedScreenProfessionSanitaDocuments
            downloadDocument={downloadDocument}
            insuranceRequest={quote.insurance_request}
            quote={quote}
            collectQuoteData={collectQuoteData}
            documentsRequests={documentsRequests}
            downloadOptionalDocument={downloadOptionalDocument}
            rc_quotation={quote}
          />
        </div>
      </div>

      {/* After Sale Section */}
      <CompletedQuoteScreenAutoAfterSale
        quote={quote}
        document_types={document_types}
        v2
      />
    </ProfessioniSanitaCompletedScreenStyles>
  );
}

export default ProfessionaleSanitaCompletedScreen;
